import { Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { parseAsArrayOf, parseAsBoolean, parseAsString, parseAsStringEnum } from "nuqs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { GqlPlannerV2Visit, GqlPlannerVisitStatus } from "~/__generated__";
import GaiaDataList from "~/modules/app/components/GaiaDataList";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import GaiaTableLinkCell from "~/modules/app/components/GaiaTableLinkCell";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatDateTime } from "~/modules/app/util/formatters";
import useRequireCurrentClient from "~/modules/client/hooks/useRequireCurrentClient";
import CheckboxInput from "~/shared/components/CheckboxInput";
import EcologicalFunctionSelect from "~/shared/components/EcologicalFunctionSelect";
import FieldworkerSelect from "~/shared/components/FieldworkerSelect";
import PlannerAreaSelect from "~/shared/components/PlannerAreaSelect";
import PlannerVisitStatusSelect from "~/shared/components/PlannerVisitStatusSelect";
import SpeciesSelect from "~/shared/components/SpeciesSelect";
import VisitStatusChip from "~/shared/components/VisitStatusChip";
import { useGridQueryState } from "~/shared/hooks/useGridQueryState";

import useRequireCurrentProject from "../../hooks/useRequireCurrentProject";
import { useProjectVisitListScreen_GetAll } from "./api/useProjectVisitListScreen_GetAll";

export default function ProjectVisitListAdminScreen() {
    const { t } = useTranslation("planner");

    const { client } = useRequireCurrentClient();
    const { project } = useRequireCurrentProject();

    const [queryState, setQueryState, gridModelProps] = useGridQueryState({
        plannerAreaIds: parseAsArrayOf(parseAsString).withDefault([]),
        plannerVisitStatus: parseAsStringEnum<GqlPlannerVisitStatus>(Object.values(GqlPlannerVisitStatus)),
        fieldworkerId: parseAsArrayOf(parseAsString).withDefault([]),
        includesObservationLeadingToEcologicalFunctionTypeId: parseAsArrayOf(parseAsString).withDefault([]),
        speciesIds: parseAsArrayOf(parseAsString).withDefault([]),
        isPossibleBatRoost: parseAsBoolean.withDefault(false),
        withoutPossibleEcologicalFunctions: parseAsBoolean.withDefault(false),
    });

    const params = useMemo(
        () => ({
            plannerAreaIds: queryState.plannerAreaIds.length > 0 ? queryState.plannerAreaIds : undefined,
            plannerVisitStatus: queryState.plannerVisitStatus ?? undefined,
            fieldworkerId: queryState.fieldworkerId.length > 0 ? queryState.fieldworkerId : undefined,
            includesObservationLeadingToEcologicalFunctionTypeId:
                queryState.includesObservationLeadingToEcologicalFunctionTypeId.length > 0
                    ? queryState.includesObservationLeadingToEcologicalFunctionTypeId
                    : undefined,
            speciesIds: queryState.speciesIds.length > 0 ? queryState.speciesIds : undefined,
            isPossibleBatRoost: queryState.isPossibleBatRoost || undefined,
            withoutPossibleEcologicalFunctions: queryState.withoutPossibleEcologicalFunctions || undefined,
            page: queryState.page,
            pageSize: queryState.pageSize,
            sortBy: queryState.sortBy,
            sortOrder: queryState.sortOrder,
        }),
        [queryState],
    );

    const { data, loading, error, total } = useProjectVisitListScreen_GetAll({
        clientId: client.id,
        projectId: project.id,
        params,
    });

    const handleParameterChange = <T,>(key: string, value: T) => {
        setQueryState({ [key]: value, page: 0 } as Parameters<typeof setQueryState>[0]);
    };

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "plannerArea.name",
                headerName: t("visitListScreen.areaName"),
                valueGetter: (_, row) => row.plannerArea?.name,
                flex: 1,
            },
            {
                field: "protocol.name",
                headerName: t("visitListScreen.protocol"),

                renderCell: ({ row }) => (
                    <Stack>
                        <Typography variant="body1">{row.protocol?.name}</Typography>
                        <Typography variant="caption">
                            {row.protocolVisit?.name ?? t("visitListScreen.visitName")} {row.protocolVisit?.number ?? ""}
                        </Typography>
                    </Stack>
                ),

                flex: 1,
            },
            {
                field: "requiredFieldworkers",
                headerName: t("visitListScreen.requiredWorkers"),
                valueGetter: (_, row) => row.requiredFieldworkers,
                flex: 1,
            },
            {
                field: "startTime",
                headerName: t("visitListScreen.startTime"),
                valueGetter: (_, row) =>
                    row.status !== GqlPlannerVisitStatus.Unplanned && row.startTime !== null
                        ? formatDateTime(parseStringToDate(row.startTime))
                        : null,
                flex: 1,
            },
            {
                field: "status",
                headerName: t("visitListScreen.status"),
                renderCell: ({ row }: GridRenderCellParams<GqlPlannerV2Visit>) => <VisitStatusChip value={row.status} />,
                flex: 1,
            },
            {
                type: "actions",
                field: "",
                maxWidth: 40,
                valueGetter: (_, visit) => {
                    if (visit.status !== GqlPlannerVisitStatus.Approved) return;

                    const clientSlug = visit.plannerArea.project.client.slug;
                    const projectSlug = visit.plannerArea.project.slug;

                    return `/c/${clientSlug}/p/${projectSlug}/visits/${visit.id}`;
                },
                renderCell: ({ value }) => (value !== undefined ? <GaiaTableLinkCell value={value} /> : undefined),
            },
        ],
        [t],
    );

    if (!data && !loading && error) {
        return <ErrorScreen error={error} />;
    }

    return (
        <GaiaPageLayoutColumn maxWidth="xl">
            <GaiaPageGridComponent title={t("visitListScreen.title")}>
                <Grid container spacing={2} sx={{ alignItems: "flex-start", "& .MuiAutocomplete-root": { width: "100%" } }}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <PlannerVisitStatusSelect
                            value={queryState.plannerVisitStatus}
                            onChange={value => handleParameterChange("plannerVisitStatus", value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <PlannerAreaSelect
                            label={t("visitListScreen.filterAreaName")}
                            value={queryState.plannerAreaIds}
                            projectIds={[project.id]}
                            onChange={value => handleParameterChange("plannerAreaIds", value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <EcologicalFunctionSelect
                            label={t("visitListScreen.filterPossibleFunctions")}
                            value={queryState.includesObservationLeadingToEcologicalFunctionTypeId}
                            onChange={value =>
                                handleParameterChange("includesObservationLeadingToEcologicalFunctionTypeId", value)
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <SpeciesSelect
                            label={t("visitListScreen.filterSpecies")}
                            multiple
                            value={queryState.speciesIds}
                            onChange={value => handleParameterChange("speciesIds", value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <FieldworkerSelect
                            label={t("visitListScreen.filterFieldworker")}
                            value={queryState.fieldworkerId}
                            onChange={value => handleParameterChange("fieldworkerId", value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <CheckboxInput
                            label={t("visitListScreen.filterIsPossibleBatRoost")}
                            value={queryState.isPossibleBatRoost}
                            onChange={value => handleParameterChange("isPossibleBatRoost", value)}
                            sx={{ alignItems: "flex-start" }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <CheckboxInput
                            label={t("visitListScreen.filterWithoutPossibleEcologicalFunctions")}
                            value={queryState.withoutPossibleEcologicalFunctions}
                            onChange={value => handleParameterChange("withoutPossibleEcologicalFunctions", value)}
                            sx={{ alignItems: "flex-start" }}
                        />
                    </Grid>
                </Grid>

                <GaiaDataList
                    data={data || []}
                    loading={loading}
                    error={error}
                    totalRows={total || 0}
                    columns={columns}
                    gridModels={gridModelProps}
                />
            </GaiaPageGridComponent>
        </GaiaPageLayoutColumn>
    );
}
